import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Chip, IconButton, MenuItem, Pagination, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { Add as AddIcon, Visibility } from '@mui/icons-material';
import { BillOfEntryService } from '../../../services/billOfEntry.service';
import { AuthService } from '../../../services/auth.service';
import { BillOfEntryListItem, BoeStatus } from '../../../interfaces/procurement/billOfEntry.interface';
import { useToast } from '../../../context/ToastContext';
import { usePermission } from '../../../hooks/usePermission';
import { PERMISSIONS } from '../../../constants/permissions';
import { formatMoney } from '../../../utils/format';
import { formatDate } from '../../../utils/dateUtils';

export const BOE_STATUS_COLORS: Record<BoeStatus, 'default' | 'success' | 'error'> = {
  DRAFT: 'default', POSTED: 'success', REVERSED: 'error',
};

export const BillOfEntryList: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const canPost = usePermission(PERMISSIONS.GOODS_RECEIPT_POST);
  const [rows, setRows] = useState<BillOfEntryListItem[]>([]);
  const [status, setStatus] = useState<BoeStatus | ''>('');
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const institutionId = AuthService.getSelectedInstitution();
    if (!institutionId) return;
    try {
      setLoading(true);
      const res = await BillOfEntryService.list(institutionId, status, page, 20);
      setRows(res.content);
      setTotalPages(res.totalPages);
    } catch (e) {
      toast.showApiError(e, 'Failed to load bills of entry');
    } finally {
      setLoading(false);
    }
  }, [status, page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  return (
    <Box sx={{ p: 3 }} data-help-page="procurement.boeList.page">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">Bills of Entry (Import Landed Cost)</Typography>
        {canPost && (
          <Button data-help="procurement.boeList.new" variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/procurement/bills-of-entry/new')}>
            New Bill of Entry
          </Button>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }} data-help-section="procurement.boeList.filter">
        <TextField data-help="procurement.boeList.filterStatus" select size="small" label="Status" value={status} sx={{ flex: '1 1 180px', maxWidth: 240 }}
          onChange={e => { setPage(0); setStatus(e.target.value as BoeStatus | ''); }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="DRAFT">Draft</MenuItem>
          <MenuItem value="POSTED">Posted</MenuItem>
          <MenuItem value="REVERSED">Reversed</MenuItem>
        </TextField>
      </Box>
      {!loading && rows.length === 0 ? (
        <Typography color="text.secondary">No bills of entry yet.</Typography>
      ) : (
        <TableContainer component={Paper} data-help-section="procurement.boeList.table">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell data-help="procurement.boeList.col.number">BoE No.</TableCell><TableCell data-help="procurement.boeList.col.date">Date</TableCell><TableCell data-help="procurement.boeList.col.house">Customs House</TableCell>
                <TableCell data-help="procurement.boeList.col.grns" align="right">GRNs</TableCell><TableCell data-help="procurement.boeList.col.cap" align="right">Capitalised</TableCell>
                <TableCell data-help="procurement.boeList.col.vat" align="right">VAT / AT credit</TableCell><TableCell data-help="procurement.boeList.col.ait" align="right">AIT</TableCell>
                <TableCell data-help="procurement.boeList.col.status">Status</TableCell><TableCell data-help="procurement.boeList.col.actions" />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.boeNumber}</TableCell>
                  <TableCell>{formatDate(r.boeDate)}</TableCell>
                  <TableCell>{r.customsHouse ?? '—'}</TableCell>
                  <TableCell align="right">{r.grnCount}</TableCell>
                  <TableCell align="right">{formatMoney(r.capitalisedTotal)}</TableCell>
                  <TableCell align="right">{formatMoney(r.inputTaxTotal)}</TableCell>
                  <TableCell align="right">{formatMoney(r.aitTotal)}</TableCell>
                  <TableCell><Chip size="small" label={r.status} color={BOE_STATUS_COLORS[r.status]} /></TableCell>
                  <TableCell>
                    <Tooltip title="View">
                      <IconButton data-help="procurement.boeList.action.view" size="small" aria-label={`View ${r.boeNumber}`}
                        onClick={() => navigate(`/procurement/bills-of-entry/${r.id}`)}><Visibility fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination count={totalPages} page={page + 1} onChange={(_e, p) => setPage(p - 1)} color="primary" />
        </Box>
      )}
    </Box>
  );
};

export default BillOfEntryList;
